/**
 * Customer Import API — RTK Query slice for extract + import.
 * Uses same /api/v1/imports/parties/insert endpoint as suppliers, with partyType = 'CUSTOMER'.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { client } from '../../../api/NestClient';
import { nestBaseQuery } from '../../../utils/nestBaseQuery';
import type { components, operations, paths } from '../../../api/types/api';

// -----------------------------------------------------------------------------
// Types (from OpenAPI — api.ts)
// -----------------------------------------------------------------------------

export type CustomerExtractRequestDto =
  paths['/api/v1/imports/excel/extract/customer']['post']['requestBody']['content']['application/json'];
export type CustomerExtractResponseDto =
  paths['/api/v1/imports/excel/extract/customer']['post']['responses']['200']['content']['application/json'];

export type CustomerImportRequestDto =
  paths['/api/v1/imports/parties/insert']['post']['requestBody']['content']['application/json'];
export type CustomerImportResponseDto =
  paths['/api/v1/imports/parties/insert']['post']['responses']['200']['content']['application/json'];
export type CustomerImportRowErrorDto = components['schemas']['PartyImportRowErrorDto'];

export type SaveCustomerDraftRequestDto = components['schemas']['SaveImportDraftRequestDto'];
export type SaveCustomerDraftResponseDto =
  operations['ImportsController_saveDraft']['responses'][200]['content']['application/json'];
export type GetCustomerDraftResponseDto =
  operations['ImportsController_getCustomerDraft']['responses'][200]['content']['application/json'];
export type DeleteCustomerDraftResponseDto =
  operations['ImportsController_deleteDraft']['responses'][200]['content']['application/json'];

// -----------------------------------------------------------------------------
// API definition & export
// -----------------------------------------------------------------------------

export const customerImportApi = createApi({
  reducerPath: 'customerImportApi',
  baseQuery: nestBaseQuery,
  endpoints: (builder) => ({
    extractCustomerExcel: builder.mutation<CustomerExtractResponseDto, CustomerExtractRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/excel/extract/customer', { body: arg }),
    }),
    /** Bulk insert parties (customers) from extract payload. */
    importCustomers: builder.mutation<CustomerImportResponseDto, CustomerImportRequestDto>({
      query: (body) =>
        client.POST('/api/v1/imports/parties/insert', {
          body,
        }),
    }),
    saveCustomerDraft: builder.mutation<SaveCustomerDraftResponseDto, SaveCustomerDraftRequestDto>({
      query: (arg) =>
        client.POST('/api/v1/imports/drafts/save', {
          body: arg,
        }),
    }),
    getCustomerDraft: builder.query<GetCustomerDraftResponseDto, void>({
      query: () => client.GET('/api/v1/imports/drafts/customers'),
    }),
    deleteCustomerDraft: builder.mutation<DeleteCustomerDraftResponseDto, void>({
      query: () =>
        client.DELETE('/api/v1/imports/drafts/{importType}', {
          params: { path: { importType: 'CUSTOMER' } },
        }),
    }),
  }),
});

export const {
  useExtractCustomerExcelMutation,
  useImportCustomersMutation,
  useSaveCustomerDraftMutation,
  useLazyGetCustomerDraftQuery,
  useDeleteCustomerDraftMutation,
} = customerImportApi;
